import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sparkles, Wand2, ImageIcon, Coins, Trophy, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { magicStatus } from "@/lib/magicApi";

/**
 * Bright neon shell for Jixxeo Magic. Consistent bottom-nav across the app.
 */
export default function MagicLayout() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/magic"); return; }
    magicStatus().then(setStatus).catch(() => {});
    const t = setInterval(() => magicStatus().then(setStatus).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [user, loading, nav]);

  return (
    <div data-testid="magic-shell" className="min-h-screen bg-[#0A0512] text-[#F5F0FF]"
      style={{ fontFamily: "'Cabinet Grotesk','Outfit',ui-sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0512]/85 border-b border-[#7A00FF]/30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/magic" className="flex items-center gap-2 min-w-0" data-testid="magic-brand">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF00E5] to-[#7A00FF] flex items-center justify-center shadow-[0_0_20px_rgba(255,0,229,0.6)]">
              <Sparkles className="w-5 h-5 text-white" fill="white" />
            </div>
            <div className="min-w-0">
              <div className="font-black text-lg leading-none">Jixxeo <span className="text-[#FF00E5]">Magic</span></div>
              <div className="text-[9px] uppercase tracking-widest text-white/50">AI photo transformer</div>
            </div>
          </Link>
          {status && (
            <div className="ml-auto flex items-center gap-2" data-testid="magic-header-status">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#FF00E5]/15 text-[#FF00E5] px-2 py-1 rounded-full border border-[#FF00E5]/30">
                <Sparkles className="w-3 h-3" fill="currentColor" /> {status.free_left}/{status.free_left + status.free_used || 3} free
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-[#FFD700]/15 text-[#FFD700] px-2 py-1 rounded-full border border-[#FFD700]/30">
                <Coins className="w-3 h-3" fill="currentColor" /> {(status.coins ?? 0).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 pb-28">
        <Outlet context={{ status, refreshStatus: () => magicStatus().then(setStatus).catch(() => {}) }} />
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0A0512]/90 border-t border-[#7A00FF]/30">
        <div className="max-w-4xl mx-auto grid grid-cols-5">
          <BottomTab to="/magic" end icon={<Wand2 className="w-5 h-5" />} label="Styles" testId="magic-nav-home" />
          <BottomTab to="/magic/feed" icon={<Users className="w-5 h-5" />} label="Feed" testId="magic-nav-feed" />
          <BottomTab to="/magic/gallery" icon={<ImageIcon className="w-5 h-5" />} label="Gallery" testId="magic-nav-gallery" />
          <BottomTab to="/magic/challenge" icon={<Trophy className="w-5 h-5" />} label="Daily" testId="magic-nav-challenge" />
          <BottomTab to="/coins" icon={<Coins className="w-5 h-5" />} label="Coins" testId="magic-nav-coins" />
        </div>
      </nav>
    </div>
  );
}

function BottomTab({ to, end, icon, label, testId }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) =>
        "flex flex-col items-center justify-center py-2.5 gap-0.5 text-[10px] uppercase tracking-widest transition-colors " +
        (isActive ? "text-[#FF00E5]" : "text-white/50 hover:text-white/80")
      }>
      {icon}
      <span className="font-bold">{label}</span>
    </NavLink>
  );
}
